import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { TextField, Container, Typography, Grid, FormControl, FormControlLabel } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import makeStyles from '@mui/styles/makeStyles';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import NcsBanner from './NcsBanner';
import { ncsSignUp, getNcsSurveyQuestions } from "api/profile";
import { emailSignIn, verifyAuth } from 'actions/authAction';
import { myFirebase } from '../../firebase/firebase';
import config from 'common/config';
import Question from 'components/Question';
import { get } from 'lodash';
import states from "./states.json";
import districts from "./districts.json";

const styles = {
  container: {
    backgroundColor: 'white',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    padding: 32,
    marginTop: 0
  },
  button: {
    backgroundColor: '#ffc220',
    fontSize: 16,
    marginTop: 20,
    textTransform: 'none',
    fontWeight: 'bold'
  },
  sectionTitle: {
    marginTop: 24,
    marginBottom: 24,
    fontSize: '14px'
  },
  declarationText: {
    marginBottom: 16,
    fontSize: '14px',
    lineHeight: 1.5
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 16
  },
  footerText: {
    marginTop: 10,
    fontSize: '12px',
  },
  fieldLabel: {
    fontSize: '12px',
    marginBottom: 8,
    marginTop: 8,
    color: '#000'
  },
  textField: {
    marginBottom: 8,
  }
};

const useStyles = makeStyles({
  disabled: {
    backgroundColor: '#e5e5e5 !important',
  },
  stateDisabled: {
    '& [class*="-control"]': {
      border: '1px solid rgba(0, 0, 0, 0.26) !important',
      borderRadius: '4px !important',
      backgroundColor: '#e5e5e5 !important',
    },
    '& [class*="-control"] > div:first-child > div:first-child': {
      color: 'rgba(0, 0, 0, 0.38) !important',
      fontSize: '16px !important',
    },
  },
});

const labelStyle = {
  color: '#000',
  padding: 0,
  margin: 0,
};

const stateLabelStyle = {
  color: '#000',
  fontSize: '12px',
  marginTop: 0,
  marginBottom: 0,
  padding: 0,
};

const MaterialStyles = theme => ({
  formWrapper: {
    padding: '0 15px',
    [theme.breakpoints.down('md')]: {
      padding: '0 10px',
    }
  }
});

const stateLookup = states.reduce((acc, s) => {
  acc[s.id] = s.name;
  return acc;
}, {});

const districtLookup = districts.reduce((acc, d) => {
  acc[d.ID] = d.Name;
  return acc;
}, {});

const stateMtgKeyLookup = states.reduce((acc, s) => {
  if (s.mtgKey) acc[s.id] = s.mtgKey;
  return acc;
}, {});

const findStateAnswerOption = (stateQuestion, stateId) => {
  const options = (stateQuestion && (stateQuestion.answerOptions || stateQuestion.answer_options)) || null;
  if (!options) return null;
  const mtgKey = stateMtgKeyLookup[stateId];
  if (!mtgKey) return null;
  const normalized = mtgKey.trim().toLowerCase();
  return options.find(
    opt => opt.key && opt.key.trim().toLowerCase() === normalized
  ) || null;
};

const NcsRegister = ({ classes }) => {
  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isSsoFlow = location.state?.isSsoFlow === true;
  const firebaseToken = location.state?.firebaseToken || '';
  const inputClasses = useStyles();

  const [agree, setAgree] = useState(false);
  const [password, setPassword] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    dateOfBirth: '',
    state: '',
    district: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [surveyQuestions, setSurveyQuestions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dynamicAnswers, setDynamicAnswers] = useState({});
  const [questionsError, setQuestionsError] = useState(false);
  const [ncsUserName, setNcsUserName] = useState('');

  useEffect(() => {
    if (!location.state?.prefillData?.email && !isSsoFlow) {
      history.push("/signin");
      return;
    }

    if (isSsoFlow) {
      const { fullName, email, mobileNumber, dateOfBirth, districtId } = location.state?.userData || {};
      const nameParts = (fullName || '').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      setFormData(prev => ({
        ...prev,
        firstName,
        lastName,
        email: email || '',
        phoneNumber: mobileNumber || '',
        dateOfBirth: dateOfBirth || '',
        district: districtLookup[districtId] || '',
      }));
    } else {
      const { email, firstName, lastName, mobileNo, dob, stateid, districtid, username } = location.state.prefillData;
      setNcsUserName(username || '');
      const formattedDob = dob ? dob.split('-').reverse().join('-') : '';
      setFormData(prev => ({
        ...prev,
        firstName: firstName || '',
        lastName: lastName || '',
        email: email || '',
        phoneNumber: mobileNo || '',
        dateOfBirth: formattedDob || '',
        state: stateLookup[stateid] || '',
        district: districtLookup[districtid] || '',
      }));
    }

    fetchSurveyQuestions();
  }, [location.state]);

  const fetchSurveyQuestions = async () => {
    try {
      setLoading(true);
      setQuestionsError(false);
      const response = await getNcsSurveyQuestions(config.NCS_INSTITUTION_CODE);
      setSurveyQuestions(response);
    } catch (error) {
      console.error('Error fetching survey questions:', error);
      setQuestionsError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDynamicAnswerChange = (answer, question) => {
    setDynamicAnswers(prev => ({ ...prev, [question.key]: answer }));
    setFormErrors(prev => ({ ...prev, [question.key]: '' }));
  };

  useEffect(() => {
    if (!isSsoFlow || !surveyQuestions || !surveyQuestions.firstStage) return;
    const stateId = location.state?.userData?.stateId;
    if (!stateId) return;
    const stateItem = getStateQuestionItem();
    if (!stateItem) return;
    const stateKey = stateItem.question.key;
    if (dynamicAnswers[stateKey]) return;
    const matchedOption = findStateAnswerOption(stateItem.question, stateId);
    if (matchedOption) {
      setDynamicAnswers(prev => ({ ...prev, [stateKey]: matchedOption }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyQuestions, isSsoFlow, location.state]);

  const getStateQuestionItem = () => {
    if (!surveyQuestions || !surveyQuestions.firstStage) return null;
    const pages = get(surveyQuestions, 'firstStage.pages', []);
    return pages.flatMap(page => page.questions || [])
      .find(q => q.question && q.question.key && q.question.key.toLowerCase().includes('state')) || null;
  };

  const validate = () => {
    const errors = {};

    if (surveyQuestions && surveyQuestions.firstStage) {
      const skippedKeys = ['dob-2', 'district'];
      const pages = get(surveyQuestions, 'firstStage.pages', []);
      pages.flatMap(page => page.questions || []).forEach(questionItem => {
        const question = questionItem.question;
        if (skippedKeys.includes(question.key)) return;
        if (questionItem.mandatory && !dynamicAnswers[question.key]) {
          errors[question.key] = t('fieldIsMandatory');
        }
      });
    }
    if (!formData.dateOfBirth) errors.dateOfBirth = t('fieldIsMandatory');
    if (!formData.district) errors.district = t('fieldIsMandatory');
    if (!isSsoFlow && !password) errors.password = t('fieldIsMandatory');
    if (!agree) errors.agree = t('fieldIsMandatory');
    return errors;
  };

  const handleEmailSignIn = async () => {
    try {
      dispatch(emailSignIn(formData.email, password));
    } catch (loginErr) {
      setFormErrors({ error: 'Signed up, but auto sign-in failed. Please try logging in.' });
    }
  };

  const handleNcsSignup = async (answers) => {
    const formattedAnswers = Object.entries(answers).map(([key, value]) => {
      let normalizedValue = value;

      if (Array.isArray(value)) {
        if (value.length && typeof value[0] === "object") {
          normalizedValue = value.map(v => v.key || String(v.id));
        }
      } else if (typeof value === "object" && value !== null) {
        normalizedValue = value.key || String(value.id);
      }

      return {
        question_key: key,
        answer: normalizedValue,
      };
    });

    const signupData = {
      role: "Mentee",
      first_name: formData.firstName,
      last_name: formData.lastName,
      institution_code: config.NCS_INSTITUTION_CODE,
      phone_number: formData.phoneNumber,
      provider: "ncs",
      new_primary_language: "en",
      email: formData.email,
      ...(isSsoFlow
        ? { ncs_sso_user_id: location.state?.userData?.ncsSsoUserId }
        : { password, ncs_username: ncsUserName }),
      survey_answers: {
        survey: config.NCS_SURVEY_ID,
        stage: config.NCS_STAGE_ID,
        answers: formattedAnswers
      }
    };

    try {
      const signupResponse = await ncsSignUp(signupData);
      if (isSsoFlow) {
        const token = signupResponse?.data?.firebaseCustomToken || firebaseToken;
        await myFirebase.auth().signInWithCustomToken(token);
        dispatch(verifyAuth(false));
      } else {
        await handleEmailSignIn();
      }
    } catch (signupError) {
      if (isSsoFlow) {
        setFormErrors({ general: 'Registration failed. Please go back and try again.' });
      } else {
        setFormErrors({ password: 'invalid' });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const errors = validate();
      setFormErrors(errors);
      if (Object.keys(errors).length === 0) {
        const mergedAnswers = {
          ...dynamicAnswers,
          'dob-2': formData.dateOfBirth,
          'district': formData.district,
        };
        await handleNcsSignup(mergedAnswers);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderDynamicQuestions = () => {
    if (loading) {
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <Typography>Loading survey questions...</Typography>
        </div>
      );
    }

    if (questionsError || !surveyQuestions || !surveyQuestions.firstStage) {
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <Typography color="error">
            Failed to load survey questions. Please refresh the page.
          </Typography>
        </div>
      );
    }

    const stateQuestionItem = getStateQuestionItem();
    const stateKey = stateQuestionItem ? stateQuestionItem.question.key : null;
    const filteredKeys = ['dob-2', 'district', ...(stateKey ? [stateKey] : [])];
    const pages = get(surveyQuestions, 'firstStage.pages', []);
    const questions = pages.flatMap(page => page.questions || [])
      .filter(q => !filteredKeys.includes(q.question.key));

    return (
      <div>
        {questions.map((questionItem) => {
          const question = { ...questionItem.question, mandatory: questionItem.mandatory };
          const error = formErrors[question.key];

          return (
            <div key={question.key} style={{ marginBottom: '16px' }}>
              <Question
                question={question}
                onAnswered={(answer) => handleDynamicAnswerChange(answer, question)}
                value={dynamicAnswers[question.key]}
                error={error}
                labelStyling={labelStyle}
                showBorder={true}
              />
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div style={{ backgroundColor: '#E5E5E5', minHeight: '100vh', paddingTop: 20, paddingBottom: 20 }}>
      <Container maxWidth={"lg"}>
        <NcsBanner />
        <form onSubmit={handleSubmit}>
          <Grid container spacing={0} style={styles.container}>
            {/* Left column */}
            <Grid item xs={12} md={4} className={classes.formWrapper}>
                            <div>
                <Typography style={styles.fieldLabel}>{t('firstName')}<span style={{ color: '#f44336' }}>*</span></Typography>
                <TextField fullWidth name="firstName" value={formData.firstName} disabled variant="outlined" inputProps={{ className: inputClasses.disabled }} />
              </div>
              <div>
                <Typography style={styles.fieldLabel}>{t('lastName')}</Typography>
                <TextField fullWidth name="lastName" value={formData.lastName} disabled variant="outlined" inputProps={{ className: inputClasses.disabled }} />
              </div>
              <div>
                <Typography style={styles.fieldLabel}>{t('emailLabelNCS')}<span style={{ color: '#f44336' }}>*</span></Typography>
                <TextField fullWidth name="email" value={formData.email} disabled variant="outlined" inputProps={{ className: inputClasses.disabled }} />
              </div>
              <div>
                <Typography style={styles.fieldLabel}>{t('phoneNumber')}<span style={{ color: '#f44336' }}>*</span></Typography>
                <TextField fullWidth name="phoneNumber" value={formData.phoneNumber} disabled variant="outlined" inputProps={{ className: inputClasses.disabled }} />
              </div>
              <div>
                <Typography style={styles.fieldLabel}>{t('dateOfBirth')}<span style={{ color: '#f44336' }}>*</span></Typography>
                <TextField
                  fullWidth
                  name="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData(prev => ({ ...prev, dateOfBirth: v }));
                    setFormErrors(prev => ({ ...prev, dateOfBirth: '' }));
                  }}
                  disabled={!isSsoFlow || !!location.state?.userData?.dateOfBirth}
                  variant="outlined"
                  inputProps={(!isSsoFlow || !!location.state?.userData?.dateOfBirth) ? { className: inputClasses.disabled } : {}}
                  InputLabelProps={{ shrink: true }}
                  error={!!formErrors.dateOfBirth}
                  helperText={formErrors.dateOfBirth}
                />
              </div>
              {(() => {
                const stateQuestionItem = getStateQuestionItem();
                if (!stateQuestionItem) return null;
                const isStateDisabled = !isSsoFlow || !!location.state?.userData?.stateId;
                const question = {
                  ...stateQuestionItem.question,
                  mandatory: stateQuestionItem.mandatory,
                  ...(isStateDisabled ? { helpText: '', localeHelpText: null } : {}),
                };
                return (
                  <div
                    style={{ marginBottom: '16px' }}
                    className={isStateDisabled ? inputClasses.stateDisabled : undefined}
                  >
                    <Question
                      question={question}
                      onAnswered={(answer) => handleDynamicAnswerChange(answer, question)}
                      value={dynamicAnswers[question.key]}
                      error={formErrors[question.key]}
                      labelStyling={isStateDisabled ? stateLabelStyle : labelStyle}
                      showBorder={true}
                      disabled={isStateDisabled}
                    />
                  </div>
                );
              })()}
              <div>
                <Typography style={styles.fieldLabel}>{t('district')}<span style={{ color: '#f44336' }}>*</span></Typography>
                <TextField
                  fullWidth
                  name="district"
                  value={formData.district}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData(prev => ({ ...prev, district: v }));
                    setFormErrors(prev => ({ ...prev, district: '' }));
                  }}
                  disabled={!isSsoFlow || !!location.state?.userData?.districtId}
                  variant="outlined"
                  inputProps={(!isSsoFlow || !!location.state?.userData?.districtId) ? { className: inputClasses.disabled } : {}}
                  error={!!formErrors.district}
                  helperText={formErrors.district}
                />
              </div>
            </Grid>

            <Grid item xs={12} md={4} className={classes.formWrapper}>
              {renderDynamicQuestions()}
            </Grid>

            <Grid item xs={12} md={4} className={classes.formWrapper}>
              <Typography variant="h6" style={styles.sectionTitle}>Declaration</Typography>
              <Typography style={styles.declarationText}>
                By submitting this form, you are agreeing for your mentoring account to be made on the
                Mentor To Go platform and you agree to the{' '}
                <a href="/termsofservice" target="_blank" style={{ textDecoration: 'none' }}>
                  Terms of Service
                </a>{' '}
                &{' '}
                <a href="/privacypolicy" target="_blank" style={{ textDecoration: 'none' }}>
                  Privacy Policy
                </a>{' '}
                of the Mentor To Go platform.
              </Typography>

              <FormControl error={!!formErrors.agree}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={agree}
                      onChange={(e) => { setAgree(e.target.checked); setFormErrors(prev => ({ ...prev, agree: '' })); }}
                      color={formErrors.agree ? "secondary" : "primary"}
                    />
                  }
                  label={<Typography style={{ fontSize: '14px', color: formErrors.agree ? 'red' : 'black' }}>I Agree</Typography>}
                />
              </FormControl>

              {!isSsoFlow && (
                <div>
                  <Typography style={styles.fieldLabel}>{t('ncsPassWord')}<span style={{ color: '#f44336' }}>*</span></Typography>
                  <TextField fullWidth type="password" value={password} onChange={(e) => { setPassword(e.target.value); setFormErrors(prev => ({ ...prev, password: '' })); }} required variant="outlined" />
                  {formErrors.password && (
                    <small style={{ color: 'red' }}>Your NCS Portal password is invalid. Please check and try again.</small>
                  )}
                </div>
              )}

              {formErrors.general && (
                <Typography style={{ color: '#f44336', fontSize: '14px', marginBottom: 8 }}>
                  {formErrors.general}
                </Typography>
              )}

              <Button type="submit" variant="contained" fullWidth style={styles.button}>
                {t('getAMentor')}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Container>
    </div>
  );
};

export default withStyles(MaterialStyles)(NcsRegister);
